import React, { useState } from 'react';
import { Check, X, Users, ChevronDown, Radio } from 'lucide-react';
import { useKiooAudio } from './AudioProvider';
import { useLiveListeners } from './useLiveListeners';

/**
 * White-label station picker: displays each station with its own logo and
 * brand accent, plus a note when the station relays another (e.g. Kilikezu).
 *
 * `variant` — visual size for the trigger button:
 *   • "chip"   (default): compact chip for use inside the top bar
 *   • "hero"   : big, high-contrast CTA meant to be very obvious (used on
 *                the Listen Live card)
 */
const StationPicker = ({ variant = 'chip' }) => {
  const { stations, currentStation, switchStation } = useKiooAudio();
  const { counts: listenerCounts } = useLiveListeners();
  const [open, setOpen] = useState(false);

  if (!stations || stations.length < 2) return null;

  const isHero = variant === 'hero';
  const triggerClass = isHero
    ? 'inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-emerald-900 text-sm font-extrabold shadow-lg ring-2 ring-white/60 hover:ring-white transition active:scale-[0.98]'
    : 'inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white text-emerald-900 text-[12px] font-extrabold shadow ring-1 ring-black/5 hover:ring-emerald-500/40 transition';

  return (
    <>
      <button
        data-testid={isHero ? 'station-picker-btn-hero' : 'station-picker-btn'}
        onClick={() => setOpen(true)}
        className={triggerClass}
        aria-label="Change station"
        style={
          isHero
            ? { color: 'var(--kioo-primary-dark)' }
            : undefined
        }
      >
        <Radio className={isHero ? 'w-4 h-4' : 'w-3.5 h-3.5'} style={{ color: 'var(--kioo-primary)' }} />
        <span className={isHero ? 'uppercase tracking-wide' : 'uppercase tracking-wide'}>
          {isHero ? 'Change station' : 'Station'}
        </span>
        <span
          className={
            isHero
              ? 'px-2 py-0.5 rounded-full text-white text-[11px] font-extrabold'
              : 'px-1.5 py-0.5 rounded-full text-white text-[10px] font-extrabold'
          }
          style={{ background: 'var(--kioo-primary)' }}
        >
          {currentStation?.short_name || currentStation?.name || 'Kioo'}
        </span>
        <ChevronDown className={isHero ? 'w-4 h-4 opacity-70' : 'w-3.5 h-3.5 opacity-70'} />
      </button>

      {open && (
        <div
          data-testid="station-picker-modal"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white rounded-2xl overflow-hidden shadow-2xl"
          >
            <div
              className="flex items-center justify-between px-4 py-3 text-white"
              style={{
                background:
                  'linear-gradient(to right, var(--kioo-primary-dark), var(--kioo-primary))',
              }}
            >
              <div>
                <div className="font-bold">Choose a station</div>
                <div className="text-[11px] opacity-80">Galcom network</div>
              </div>
              <button
                data-testid="station-picker-close"
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <ul className="p-2 max-h-[70vh] overflow-y-auto">
              {stations.map((s) => {
                const active = s.slug === currentStation?.slug;
                const isRelay = !s.has_own_stream;
                return (
                  <li key={s.slug}>
                    <button
                      data-testid={`station-option-${s.slug}`}
                      onClick={() => {
                        switchStation(s.slug);
                        setOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition border ${
                        active
                          ? 'border-2'
                          : 'border-transparent hover:bg-gray-50'
                      }`}
                      style={
                        active
                          ? {
                              backgroundColor: s.colors?.background || '#ecfdf5',
                              borderColor: s.colors?.primary || '#16a34a',
                            }
                          : {}
                      }
                    >
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 p-1"
                        style={{
                          background:
                            s.colors?.background || '#f3f4f6',
                          border: `1px solid ${s.colors?.primary || '#e5e7eb'}22`,
                        }}
                      >
                        {s.logo_url ? (
                          <img
                            src={s.logo_url}
                            alt={s.name}
                            className="w-full h-full object-contain"
                            loading="lazy"
                          />
                        ) : (
                          <span className="text-xs">📻</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-bold text-gray-900 truncate">
                            {s.name}
                          </div>
                          {s.frequency && (
                            <span
                              className="text-[10px] font-extrabold px-1.5 py-0.5 rounded"
                              style={{
                                background: s.colors?.primary,
                                color: s.colors?.text_on_primary,
                              }}
                            >
                              {s.frequency}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {s.tagline}
                        </div>
                        <div className="text-[10px] text-gray-400 truncate mt-0.5">
                          {s.country} · {s.language}
                        </div>
                        {isRelay && s.relay_note && (
                          <div
                            data-testid={`station-relay-note-${s.slug}`}
                            className="text-[10px] text-amber-700 mt-1 font-medium"
                          >
                            🔁 {s.relay_note}
                          </div>
                        )}
                        <div
                          data-testid={`station-listeners-${s.slug}`}
                          className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-gray-600"
                        >
                          <Users className="w-3 h-3" />
                          {(listenerCounts[s.slug] || 0).toLocaleString()} listening now
                        </div>
                      </div>
                      {active && (
                        <Check
                          data-testid={`station-active-${s.slug}`}
                          className="w-5 h-5 flex-shrink-0"
                          style={{ color: s.colors?.primary }}
                        />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default StationPicker;
